import { useMemo } from 'react';
import { VALUE_CHAIN_AI_AGENT, VALUE_CHAIN_MONITORING_AGENT } from 'constants/general';
import {
  getAIGeneratingSessionStatus,
  isVcAgentBomSocketInProgressStatus,
} from 'utils/AICreatorHelper';

import {
  isMonitoringSessionProcessingStatus,
  isMonitoringSessionType,
} from '../../../../../../components/MonitoringAgent/utils/monitoringSessionFlowUtils';
import { resolveExistingMonitoringSession } from '../../../../../../components/MonitoringAgent/utils/monitoringSessionUtils';
import {
  hasAgentSessionProcessingStatus,
  isDraftReviewReadyStatus,
  isRestorableAIGeneratingSession,
} from '../utils/valueChainAgentSessions';

import type ISessionVCItem from '../../../../../../store/slices/valueChainMap/interfaces/ISessionVCItem';
import type { IUseValueChainAgentSessionStateParams } from '../interfaces/IUseValueChainAgentSessionState';

const useValueChainAgentSessionState = ({
  agentSessions,
  sessionId,
  activeAgentSession,
  ignoredExistingSessionId,
  existingSessionStatusOverride,
  monitoringSessionType,
  openAISessionsSidebar,
  bomUnitsSession,
  supplierUnitsSession,
  draftReviewSession,
  isAIGeneratingFloatingButtonEnabled,
  hasUnreadAIGeneratingResults,
  isSupplierGeneratingPending,
  lastBomSocketPayload,
}: IUseValueChainAgentSessionStateParams) => {
  const rawExistingSession = useMemo(() => {
    const activeSessionId = sessionId || activeAgentSession?.id;

    if (activeSessionId) {
      const matchedSession = agentSessions?.find(
        (sessionItem) => sessionItem.id === activeSessionId,
      );

      if (matchedSession) {
        return matchedSession;
      }

      return activeAgentSession?.id === activeSessionId ? activeAgentSession : undefined;
    }

    return agentSessions?.find((sessionItem) => (
      sessionItem.type === VALUE_CHAIN_AI_AGENT.SESSION_TYPE && sessionItem.isActive
    )) || agentSessions?.find(
      (sessionItem) => sessionItem.type === VALUE_CHAIN_AI_AGENT.SESSION_TYPE,
    );
  }, [activeAgentSession, sessionId, agentSessions]);

  const existingSession = useMemo(() => {
    if (
      !rawExistingSession
      || rawExistingSession.id === ignoredExistingSessionId
      || existingSessionStatusOverride?.sessionId !== rawExistingSession.id
    ) {
      if (rawExistingSession?.id === ignoredExistingSessionId) {
        return undefined;
      }

      return rawExistingSession;
    }

    return {
      ...rawExistingSession,
      status: existingSessionStatusOverride.status,
    };
  }, [existingSessionStatusOverride, ignoredExistingSessionId, rawExistingSession]);

  const currentAISession = useMemo(() => (
    activeAgentSession || agentSessions?.find((sessionItem) => (
      sessionItem.type === VALUE_CHAIN_AI_AGENT.SESSION_TYPE && sessionItem.isActive
    ))
  ), [activeAgentSession, agentSessions]);
  const currentAISessionSelectedUnits = currentAISession?.selectedUnits;
  const aiSessionPreviewSelectedUnitIds = useMemo<ReadonlySet<number>>(() => {
    if (!openAISessionsSidebar || !currentAISessionSelectedUnits?.length) {
      return new Set<number>();
    }

    return new Set(currentAISessionSelectedUnits.map(({ unitId }) => unitId));
  }, [currentAISessionSelectedUnits, openAISessionsSidebar]);

  const liveBomUnitsSession = useMemo(() => {
    if (!bomUnitsSession?.id) {
      return bomUnitsSession;
    }

    return agentSessions?.find(
      (sessionItem) => sessionItem.id === bomUnitsSession.id,
    ) || bomUnitsSession;
  }, [bomUnitsSession, agentSessions]);

  const liveSupplierUnitsSession = useMemo(() => {
    if (!supplierUnitsSession?.id) {
      return supplierUnitsSession;
    }

    return agentSessions?.find(
      (sessionItem) => sessionItem.id === supplierUnitsSession.id,
    ) || supplierUnitsSession;
  }, [supplierUnitsSession, agentSessions]);

  const liveDraftReviewSession = useMemo(() => {
    if (!draftReviewSession?.id) {
      return draftReviewSession;
    }

    return agentSessions?.find(
      (sessionItem) => sessionItem.id === draftReviewSession.id,
    ) || draftReviewSession;
  }, [draftReviewSession, agentSessions]);
  const isLiveDraftReviewSessionReady = isDraftReviewReadyStatus(liveDraftReviewSession?.status);

  const hasSupplierNTierAttention = (agentSessions || []).some(
    (sessionItem) => sessionItem.type === VALUE_CHAIN_AI_AGENT.SESSION_TYPE,
  );

  const hasReputationMonitoringAttention = (agentSessions || []).some(
    (sessionItem) => sessionItem.type === VALUE_CHAIN_MONITORING_AGENT.REPUTATION_SESSION_TYPE,
  );

  const hasRegulationMonitoringAttention = (agentSessions || []).some(
    (sessionItem) => sessionItem.type === VALUE_CHAIN_MONITORING_AGENT.REGULATION_SESSION_TYPE,
  );

  const hasFinanceMonitoringAttention = (agentSessions || []).some(
    (sessionItem) => sessionItem.type === VALUE_CHAIN_MONITORING_AGENT.FINANCE_SESSION_TYPE,
  );

  const hasOwnershipMonitoringAttention = (agentSessions || []).some(
    (sessionItem) => sessionItem.type === VALUE_CHAIN_MONITORING_AGENT.OWNERSHIP_SESSION_TYPE,
  );

  const hasClimateMonitoringAttention = (agentSessions || []).some(
    (sessionItem) => sessionItem.type === VALUE_CHAIN_MONITORING_AGENT.CLIMATE_SESSION_TYPE,
  );

  const existingMonitoringSession = useMemo(() => resolveExistingMonitoringSession({
    sessions: agentSessions,
    sessionId,
    activeAgentSession,
    sessionType: monitoringSessionType,
  }), [activeAgentSession, agentSessions, monitoringSessionType, sessionId]);

  const effectiveMonitoringSession = useMemo(() => {
    if (
      !existingMonitoringSession
      || existingSessionStatusOverride?.sessionId !== existingMonitoringSession.id
    ) {
      return existingMonitoringSession;
    }

    return {
      ...existingMonitoringSession,
      status: existingSessionStatusOverride.status,
    };
  }, [existingMonitoringSession, existingSessionStatusOverride]);

  const isMonitoringSessionGenerating = useMemo(
    () => Boolean(
      effectiveMonitoringSession
      && isMonitoringSessionProcessingStatus(effectiveMonitoringSession.status),
    ),
    [effectiveMonitoringSession],
  );

  const hasProcessingAgentSessions = useMemo(() => (
    (agentSessions || []).some(hasAgentSessionProcessingStatus)
  ), [agentSessions]);

  const hasRestorableAIGeneratingSessions = useMemo(() => (
    (agentSessions || []).some((sessionItem) => {
      if (!isRestorableAIGeneratingSession(sessionItem) || sessionItem.id === ignoredExistingSessionId) {
        return false;
      }

      const sessionWithStatusOverride = existingSessionStatusOverride?.sessionId === sessionItem.id
        ? { ...sessionItem, status: existingSessionStatusOverride.status }
        : sessionItem;

      return isDraftReviewReadyStatus(sessionWithStatusOverride.status)
        || hasAgentSessionProcessingStatus(sessionWithStatusOverride);
    })
  ), [
    existingSessionStatusOverride,
    ignoredExistingSessionId,
    agentSessions,
  ]);

  const hasAIGeneratingSessions = useMemo(() => {
    if (!isAIGeneratingFloatingButtonEnabled && !hasRestorableAIGeneratingSessions) {
      return false;
    }

    const hasProcessingSessions = (agentSessions || []).some(
      (sessionItem) => getAIGeneratingSessionStatus(sessionItem, lastBomSocketPayload)
        === VALUE_CHAIN_AI_AGENT.STATUS_IN_PROGRESS,
    );

    return hasRestorableAIGeneratingSessions
      || hasProcessingSessions
      || hasUnreadAIGeneratingResults
      || isSupplierGeneratingPending;
  }, [
    hasRestorableAIGeneratingSessions,
    isAIGeneratingFloatingButtonEnabled,
    hasUnreadAIGeneratingResults,
    isSupplierGeneratingPending,
    lastBomSocketPayload,
    agentSessions,
  ]);

  const hasActiveAgentSessionProcessing = useMemo(() => (
    (agentSessions || []).some((sessionItem) => {
      const isSupplierDiscoverySession = sessionItem.subType
        === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE;

      if (isSupplierDiscoverySession && !isSupplierGeneratingPending) {
        return false;
      }

      return isVcAgentBomSocketInProgressStatus(sessionItem.status)
        || sessionItem.selectedUnits?.some((selectedUnit) => (
          isVcAgentBomSocketInProgressStatus(selectedUnit.status)
        )) || false;
    })
    || (agentSessions || []).some((sessionItem) => (
      isMonitoringSessionType(sessionItem.type)
      && isMonitoringSessionProcessingStatus(sessionItem.status)
    ))
  ), [isSupplierGeneratingPending, agentSessions]);

  const hasPollingProcessingStatuses = useMemo(() => {
    const sessionsToInspect = [
      existingSession,
      existingMonitoringSession,
      liveBomUnitsSession,
      liveSupplierUnitsSession,
      draftReviewSession,
    ]
      .filter((sessionItem): sessionItem is ISessionVCItem => Boolean(sessionItem))
      .map((sessionItem) => (
        agentSessions?.find((candidate) => candidate.id === sessionItem.id) || sessionItem
      ));

    return sessionsToInspect.some(hasAgentSessionProcessingStatus);
  }, [
    draftReviewSession,
    existingMonitoringSession,
    existingSession,
    liveBomUnitsSession,
    liveSupplierUnitsSession,
    agentSessions,
  ]);

  return {
    rawExistingSession,
    existingSession,
    currentAISession,
    aiSessionPreviewSelectedUnitIds,
    liveBomUnitsSession,
    liveSupplierUnitsSession,
    liveDraftReviewSession,
    isLiveDraftReviewSessionReady,
    hasSupplierNTierAttention,
    hasReputationMonitoringAttention,
    hasRegulationMonitoringAttention,
    hasFinanceMonitoringAttention,
    hasOwnershipMonitoringAttention,
    hasClimateMonitoringAttention,
    existingMonitoringSession,
    effectiveMonitoringSession,
    isMonitoringSessionGenerating,
    hasProcessingAgentSessions,
    hasRestorableAIGeneratingSessions,
    hasAIGeneratingSessions,
    hasActiveAgentSessionProcessing,
    hasPollingProcessingStatuses,
  };
};

export default useValueChainAgentSessionState;
